import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from "axios"
import { AUTH_ENDPOINTS, AUTH_STORAGE_KEYS } from "@/shared/config/auth"
import { refreshToken } from "./refresh-token"

let isRefreshing = false
let failedQueue: Array<{
	resolve: (value?: unknown) => void
	reject: (reason?: unknown) => void
}> = []

let reauthHandler: (() => void) | null = null

/** Registers a callback invoked when a request fails auth recovery and the app must re-authenticate. */
export const setReauthHandler = (handler: () => void): void => {
	reauthHandler = handler
}

const processQueue = (error: Error | null, token: string | null = null): void => {
	for (const prom of failedQueue) {
		if (error) {
			prom.reject(error)
		} else {
			prom.resolve(token)
		}
	}

	failedQueue = []
}

const isAuthEndpoint = (url?: string): boolean => {
	if (!url) return false
	return url.includes(AUTH_ENDPOINTS.LOGIN) || url.includes(AUTH_ENDPOINTS.REFRESH)
}

const AUTH_REDIRECT_KEY = "auth_redirect_ts"
const AUTH_REDIRECT_COOLDOWN_MS = 5_000

const clearAuthAndReauth = (): void => {
	const lastRedirect = sessionStorage.getItem(AUTH_REDIRECT_KEY)
	const now = Date.now()

	if (lastRedirect && now - Number(lastRedirect) < AUTH_REDIRECT_COOLDOWN_MS) {
		return
	}

	sessionStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN)
	sessionStorage.removeItem(AUTH_STORAGE_KEYS.USER)
	sessionStorage.setItem(AUTH_REDIRECT_KEY, String(now))

	if (reauthHandler) {
		reauthHandler()
	} else {
		window.location.href = "/"
	}
}

const attachAccessToken = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
	const token = sessionStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN)
	if (token) {
		config.headers.Authorization = `Bearer ${token}`
	}
	return config
}

const retryWithRefreshedToken = async (
	client: AxiosInstance,
	originalRequest: InternalAxiosRequestConfig & { _retry?: boolean },
): Promise<unknown> => {
	if (originalRequest._retry) {
		return Promise.reject(new Error("Unauthorized"))
	}

	const currentToken = sessionStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN)
	if (!currentToken) {
		return Promise.reject(new Error("Unauthorized"))
	}

	if (isAuthEndpoint(originalRequest.url)) {
		return Promise.reject(new Error("Unauthorized"))
	}

	if (isRefreshing) {
		return new Promise((resolve, reject) => {
			failedQueue.push({ resolve, reject })
		})
			.then((token) => {
				originalRequest.headers.Authorization = `Bearer ${token}`
				return client(originalRequest)
			})
			.catch((err) => Promise.reject(err))
	}

	originalRequest._retry = true
	isRefreshing = true

	try {
		const data = await refreshToken()
		const newToken = data.accessToken

		sessionStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, newToken)
		processQueue(null, newToken)

		originalRequest.headers.Authorization = `Bearer ${newToken}`
		return client(originalRequest)
	} catch (refreshError) {
		processQueue(refreshError as Error, null)
		clearAuthAndReauth()
		return Promise.reject(refreshError)
	} finally {
		isRefreshing = false
	}
}

/**
 * Creates an Axios client that automatically attaches the access token and
 * transparently refreshes it on 401 responses, queuing concurrent requests
 * while a refresh is in flight.
 */
export function createAuthenticatedClient(
	baseURL: string,
	options?: { contentType?: string },
): AxiosInstance {
	const client = axios.create({
		baseURL,
		withCredentials: true,
		...(options?.contentType ? { headers: { "Content-Type": options.contentType } } : {}),
	})

	client.interceptors.request.use(attachAccessToken)
	client.interceptors.response.use(
		(response) => response,
		async (error) => {
			const originalRequest = error.config as InternalAxiosRequestConfig & {
				_retry?: boolean
			}

			if (error.response?.status !== 401 || !originalRequest) {
				return Promise.reject(error)
			}

			return retryWithRefreshedToken(client, originalRequest)
		},
	)

	return client
}
